import React from 'react';
import { CalendarBlank, User } from '@phosphor-icons/react';

interface CardListProps {
    data: Array<{
        goal_name: string;
        status: string;
        progress: number;
        due_date: string;
        owner: string;
    }>;
    options?: {
        show_progress?: boolean;
        show_due_date?: boolean;
    };
}

const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
        case 'completed': return 'bg-green-100 text-green-700';
        case 'in_progress': return 'bg-blue-100 text-blue-700';
        case 'not_started': return 'bg-gray-100 text-gray-700';
        case 'at_risk': return 'bg-red-100 text-red-700';
        default: return 'bg-gray-100 text-gray-700';
    }
};

export const CardList: React.FC<CardListProps> = ({ data, options = {} }) => {
    const { show_progress = true, show_due_date = true } = options;

    if (!data || data.length === 0) {
        return <div className="flex items-center justify-center h-full text-gray-400 text-sm">No goals found</div>;
    }

    return (
        <div className="grid grid-cols-1 gap-3 p-2 overflow-auto h-full">
            {data.map((item, index) => (
                <div
                    key={`${item.goal_name}-${index}`}
                    className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
                >
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2" title={item.goal_name}>
                            {item.goal_name}
                        </h3>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${getStatusColor(item.status)}`}>
                            {item.status.replace(/_/g, ' ')}
                        </span>
                    </div>

                    {show_progress && (
                        <div className="mb-3">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>Progress</span>
                                <span>{item.progress}%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5">
                                <div
                                    className="bg-blue-500 h-1.5 rounded-full"
                                    style={{ width: `${item.progress}%` }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-50">
                        <div className="flex items-center gap-1">
                            <User size={12} />
                            <span>{item.owner}</span>
                        </div>
                        {show_due_date && (
                            <div className="flex items-center gap-1">
                                <CalendarBlank size={12} />
                                <span>{new Date(item.due_date).toLocaleDateString()}</span>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};
